// Single source of truth for per-package money maths.
//
// This is the pure costing logic that used to live inline in
// stages/configure/package/package.js. It is extracted here so the same
// calculation can be reused by the date-flexibility scanner (which has to
// recompute whole-trip totals for shifted check-in dates without mounting a
// Package component). package.js calls this directly so there is only ever one
// implementation of the maths.
//
// `details` is the package/price-window object. `selections` carries the
// agent's choices for that package:
//   { chosenRoomType, chosenBoardType, chosenFeeType,   // { option } shapes
//     buggiesIncluded, buggiesCost,
//     hasAdditionalNights, additionalNights, additionalNightsCost,
//     discountsChosen,                                  // array of { type, value }
//     groupSize }
//
// Returns the per-person package total in the package's own currency, matching
// exactly what package.js produced before the extraction.
export const calculatePackageCost = (details, selections = {}) => {
	const {
		chosenRoomType = {},
		chosenBoardType = {},
		chosenFeeType = {},
		buggiesIncluded = false,
		buggiesCost = null,
		hasAdditionalNights = false,
		additionalNights = null,
		additionalNightsCost = null,
		discountsChosen = [],
		groupSize = null,
	} = selections

	// Setup a running total
	let runningTotal = 0

	if (details.price_type === "resort" && details.room_types?.length > 0) {
		const matchingRoom = details.room_types.find((room) => room.type === chosenRoomType.option)
		if (matchingRoom?.cost) {
			runningTotal += parseFloat(matchingRoom.cost)
		}
	}

	if (details.price_type === "resort" && details.board_types?.length > 0) {
		const matchingBoard = details.board_types.find((board) => board.type === chosenBoardType.option)
		if (matchingBoard?.cost) {
			runningTotal += parseFloat(matchingBoard.cost)
		}
	}

	if (details.price_type === "course" && details.fee_types?.length > 0) {
		const matchingFeeType = details.fee_types.find((fee) => fee.type === chosenFeeType.option)
		if (matchingFeeType?.cost) {
			runningTotal += parseFloat(matchingFeeType.cost)
		}
	}

	if (buggiesIncluded) {
		if (!isNaN(parseFloat(buggiesCost)) && buggiesCost !== null) {
			runningTotal += parseFloat(buggiesCost)
		}
	}

	let nightsMultiplier = 1
	if (details.type === "per_night") {
		nightsMultiplier = details.nights
	}

	// Times the total cost for the package based on the nights passed in
	runningTotal *= nightsMultiplier

	if (hasAdditionalNights) {
		if (!isNaN(parseInt(additionalNights)) && additionalNights !== null) {
			if (!isNaN(parseFloat(additionalNightsCost)) && additionalNightsCost !== null) {
				runningTotal += parseInt(additionalNights) * parseFloat(additionalNightsCost)
			}
		}
	}

	// If there is a selected discount in the state
	if (discountsChosen?.length > 0) {
		discountsChosen.forEach((discountChosen) => {
			// Pull the details from the object
			const { type, value } = discountChosen

			// Are we looking at a pax type of discount?
			if (type === "pax") {
				// Parse the required group size as an integer
				const discountGroupSize = parseInt(value)

				// Work out how many guests will be going for free
				const goingFree = Math.floor(groupSize / discountGroupSize)
				const adjustedGroupSize = groupSize - goingFree

				// Calculate teh adjusted per-person price
				const adjustedPrice = (runningTotal * adjustedGroupSize) / groupSize

				// If we get a valid integer at the end of the calculation
				if (!isNaN(adjustedPrice)) {
					runningTotal = Math.round(adjustedPrice)
				}
			}

			// Is it a percentage discount?
			if (type === "percent") {
				// Work out the interim total without the buggies cost
				const interimTotal = buggiesIncluded ? runningTotal - parseFloat(buggiesCost) : runningTotal

				// Parse the discount value as a float
				const asFloat = parseFloat(value)

				// Then divide this by 100 and subtract from 1 to give our calculation
				const adjustedPercentage = 1 - asFloat / 100

				// Are we still dealing with a calculated figure
				if (!isNaN(adjustedPercentage)) {
					// Use this to multiply against the interim total
					runningTotal = Math.round(interimTotal * adjustedPercentage)

					// Then add the buggies cost back on if they were included
					if (buggiesIncluded && buggiesCost > 0) {
						runningTotal += parseFloat(buggiesCost || 0)
					}
				}
			}
		})
	}

	return runningTotal
}
